import { Link } from 'react-router-dom';
import { PageHeader } from '../components/Layout';
import { Badge, Button, Card, EmptyState, ErrorNote, Loading } from '../components/ui';
import { api, type TodayItem, type TodayItemKind } from '../lib/api';
import { relative } from '../lib/format';
import { useAction, useApi } from '../lib/hooks';

const KIND_LABEL: Record<TodayItemKind, string> = {
  next_step: 'Next steps',
  reply: 'Replies awaiting action',
  sequence_step: 'Sequence steps due',
  stale_deal: 'Deals gone quiet',
  renewal: 'Renewals coming up',
};

const KIND_TONE: Record<TodayItemKind, 'purple' | 'blue' | 'amber' | 'red' | 'green'> = {
  next_step: 'purple',
  reply: 'blue',
  sequence_step: 'amber',
  stale_deal: 'red',
  renewal: 'green',
};

export function Today() {
  const list = useApi<{ items: TodayItem[] }>('/api/today');
  const items = list.data?.items ?? [];

  const grouped: Record<TodayItemKind, TodayItem[]> = {
    next_step: [],
    reply: [],
    sequence_step: [],
    stale_deal: [],
    renewal: [],
  };
  for (const item of items) grouped[item.kind].push(item);

  return (
    <>
      <PageHeader
        title="Today"
        description="What needs your attention: Claude-suggested next steps, replies you haven't acted on, sequence steps due, open deals that have gone quiet, and retainers coming up for renewal — the by-product of everything else tracking its own state, not a separate task list."
      />

      {list.loading ? (
        <Loading />
      ) : list.error ? (
        <ErrorNote message={list.error} onRetry={list.reload} />
      ) : items.length === 0 ? (
        <EmptyState title="Nothing due" description="Nothing needs your attention right now." />
      ) : (
        <div className="space-y-4">
          {(Object.keys(grouped) as TodayItemKind[]).map((kind) =>
            grouped[kind].length === 0 ? null : (
              <Card key={kind} title={`${KIND_LABEL[kind]} (${grouped[kind].length})`} bodyClassName="p-0">
                <ul className="divide-y divide-ink-100">
                  {grouped[kind].map((item) => (
                    <TodayRow key={`${item.kind}:${item.ref}:${item.prospect_id}`} item={item} onChanged={list.reload} />
                  ))}
                </ul>
              </Card>
            ),
          )}
        </div>
      )}
    </>
  );
}

function TodayRow({ item, onChanged }: { item: TodayItem; onChanged: () => void }) {
  const snooze = useAction(async () => {
    // Quiet deals and renewals are slow-burn: a day's snooze would bring them straight back.
    const hours = item.kind === 'stale_deal' || item.kind === 'renewal' ? 24 * 7 : undefined;
    await api.post(`/api/today/${item.kind}/${encodeURIComponent(item.ref)}/snooze`, { prospect_id: item.prospect_id, hours });
    onChanged();
  });
  const done = useAction(async () => {
    await api.post(`/api/today/${item.kind}/${encodeURIComponent(item.ref)}/done`, { prospect_id: item.prospect_id });
    onChanged();
  });

  return (
    <li className="flex flex-wrap items-start justify-between gap-3 px-4 py-3">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={KIND_TONE[item.kind]}>{KIND_LABEL[item.kind]}</Badge>
          <Link to={`/prospects/${item.prospect_id}`} className="text-sm font-medium text-accent-700 hover:underline">
            {item.prospect_name}
          </Link>
          {item.company_name && <span className="text-xs text-ink-500">{item.company_name}</span>}
          {item.due_at && <span className="ml-auto text-xs text-ink-400">{relative(item.due_at)}</span>}
        </div>
        <p className="mt-1 text-sm text-ink-800">{item.title}</p>
        {item.detail && <p className="mt-0.5 text-xs text-ink-500">{item.detail}</p>}
        {(snooze.error || done.error) && (
          <div className="mt-2"><ErrorNote message={(snooze.error ?? done.error) as string} /></div>
        )}
      </div>
      <div className="flex shrink-0 gap-2">
        <Button size="sm" variant="ghost" loading={snooze.pending} onClick={() => void snooze.run()}>
          Snooze
        </Button>
        <Button size="sm" loading={done.pending} onClick={() => void done.run()}>
          Done
        </Button>
      </div>
    </li>
  );
}
